/**
 * Bounds for everything that arrives from outside the app
 * (specs/features/untrusted-input.md, ADR-015).
 *
 * A song is a *shareable document* — it travels as a `#song=` URL, a file, a
 * pasted AI reply or a `.websynth.zip`, and the link forms apply at boot with no
 * user interaction. So every payload is authored by someone else, and ADR-003
 * (zero runtime dependencies) means no library sets a sane default limit on our
 * behalf: every bound is ours to write down, and this is where they are written.
 *
 * Sized against the demo corpus **plus generous headroom**, deliberately: a limit
 * is a compatibility surface, and refusing a real song is a worse failure than
 * accepting a slightly silly one. Raising a limit later is additive and safe;
 * lowering one is a breaking change (ADR-007's treatment applies).
 */

/** Decoded `#song=` payload / fetched `songUrl` body. The largest demo is ~2 orders below. */
export const MAX_SONG_JSON_BYTES = 8 * 1024 * 1024;

/** One decompressed zip entry — a sampler clip is a multi-MB WAV. */
export const MAX_ZIP_ENTRY_BYTES = 64 * 1024 * 1024;

/** Summed across all entries of one zip: 8 slots of clip audio plus headroom. */
export const MAX_ZIP_TOTAL_BYTES = 256 * 1024 * 1024;

/** Central-directory entry count: 8 clips + song.json + folder entries, generously. */
export const MAX_ZIP_ENTRIES = 64;

/** A WebRTC signalling blob (`WS2.…`) or a scanned QR. A deflated SDP is ~700 bytes. */
export const MAX_SIGNAL_BYTES = 256 * 1024;

/**
 * How far ahead of now a WiFi peer's `start`/`continue` time may point
 * (midi-clock-sync.md REQ-a-join-is-timed-by-its-first-pulse). A real join is at most one look-ahead
 * (~225 ms) ahead; clamping stops a hostile peer parking a slave indefinitely.
 */
export const MAX_SYNC_JOIN_LEAD_MS = 1000;

/** Arrangement chain length. 1024 bars is ~34 minutes at 120 BPM. */
export const MAX_CHAIN_STEPS = 1024;

/** `{enabled, steps: {enabled, steps: …}}` nesting in the authoring dialect. */
export const MAX_CHAIN_DEPTH = 8;

/**
 * Per-slot arrangement transpose, in semitones (arrangement.md REQ-a-seq-slot-carries-a-transpose).
 * ±2 octaves — the same span as `drum.t*.tune`, and enough for any progression.
 * Kept modest deliberately: the offset is added to a stored note before the
 * `MIDI_NOTE_MIN/MAX` clamp, so a huge range would just clamp into silence at
 * the edges while making the UI's readout wider for no musical gain.
 */
export const MAX_CHAIN_TRANSPOSE = 24;

/**
 * Transport position, in 16th-note ticks (transport.md REQ-the-step-counter-is-bounded-at-ingress).
 *
 * `Clock._step` used to be masked with `& 0xffff`, which doubled as its bound.
 * That wrap was only phase-safe for bar lengths dividing 65536 — i.e. powers of
 * two — so meter.md REQ-the-step-counter-must-not-wrap had to remove it, and the bound moved here. `2**31` is
 * ~8.5 years of 16ths at 120 BPM: a guard rail against a hostile `seek`, not a
 * limit any song can reach.
 */
export const MAX_STEP = 2 ** 31;

/** Keys in a `params` map. The bus registers ~150; unknown ids are kept (ADR-007). */
export const MAX_PARAM_KEYS = 512;

/**
 * A song, preset or bank title, in code points (untrusted-input.md
 * REQ-names-are-capped). Enough for any real title; shortened, never refused.
 */
export const MAX_NAME_CHARS = 50;

/**
 * The public MCP endpoint (untrusted-input.md REQ-the-public-endpoint-is-bounded, ADR-020). Everything above
 * is sized *generously* — refusing a real song is the worse failure. These four
 * are sized the other way, and the difference is deliberate: every other surface
 * is reached by a user who chose to open something, while
 * `https://vast.status201.com/mcp` is authless and reachable by anyone forever.
 * There a limit is not a compatibility surface, it is the only defence, so it
 * costs nothing to be tight and it costs real money to be loose.
 */

/**
 * One `POST /mcp` body. An eighth of {@link MAX_SONG_JSON_BYTES}: a public
 * endpoint pays CPU for everything it parses, and no authored song is near
 * either number. Enforced against a *running* count while the socket is read,
 * per REQ-a-curve-carries-rate-not-position — a cap applied after buffering has already spent the memory.
 */
export const MAX_MCP_REQUEST_BYTES = 1024 * 1024;

/** Per-IP fixed-window request budget. */
export const MAX_MCP_REQUESTS_PER_MINUTE = 60;

/**
 * IPs the rate limiter may track at once, after which it evicts.
 *
 * The limiter is itself a payload-reachable data structure: it allocates one
 * entry per caller-chosen key, so without this it is a memory-exhaustion vector
 * wearing a defence's clothes. ADR-015's rule applies to the guard as much as to
 * the thing guarded.
 */
export const MAX_MCP_RATE_KEYS = 10_000;

/** Wall clock for one MCP request, socket to response. */
export const MAX_MCP_REQUEST_MS = 15_000;

/**
 * A Standard MIDI File (midi-file-reader.md REQ-a-midi-file-is-bounded), checked
 * before a byte is parsed. A real song's .mid is tens of kB.
 */
export const MAX_MIDI_FILE_BYTES = 512 * 1024;

/** `MTrk` chunks in one file; a full orchestral export has about 20. */
export const MAX_MIDI_TRACKS = 64;

/**
 * Bars in the analysis bar map. Without it one note at tick 2^28 asks for
 * millions of bars — the file is a few bytes, the work is not.
 */
export const MAX_MIDI_BARS = 4096;

/** Bars one `read_midi` listing returns; the agent pages on with `fromBar`. */
export const MAX_MIDI_SUMMARY_BARS = 64;

/** A track name carried into the reply: the one free-text field a file brings into an agent's context. */
export const MAX_MIDI_NAME_CHARS = 64;

/** MIDI note range. `midiToHz(1e6)` is Infinity, and a non-finite AudioParam write throws. */
export const MIDI_NOTE_MIN = 0;
export const MIDI_NOTE_MAX = 127;

/**
 * Per-step micro-timing resolution: notches per **cell** (step-settings.md REQ-a-step-carries-a-micro-offset).
 * 24 makes a 1/384 note at the default lane rate — the same grid Elektron's Micro
 * Timing uses — and, unlike 16, puts the triplet positions exactly on a notch
 * (`8/24` is a third of a cell).
 */
export const MICRO_UNITS = 24;

/**
 * Sub-hits within one step (step-settings.md). Four is the ceiling because the
 * fifth would land on the next step's onset at the default lane rate, and
 * because the row of ratchet buttons is drawn from this number.
 *
 * Here for the same reason as {@link MICRO_MAX}, the other per-step integer
 * bound: it was a literal `4` in the canonical validator, in the dialect's
 * coercing twin, in the editor's button loop and in the published JSON schema —
 * four places to change, in three languages, for one rule
 * (untrusted-input.md REQ-the-limits-are-one-module). The minimum stays a
 * literal 1: "at least one hit" is what a step *is*, not a tunable.
 */
export const MAX_RATCHET = 4;

/**
 * The bound on `micro`, in notches: half a cell either way (step-settings.md
 * REQ-micro-range-is-half-a-cell). Chosen because it is exactly the point at which a fully-late step and
 * the fully-early step after it *meet* rather than **cross** — which is what keeps
 * the ducker's `when < onset` guard and the sequencer's mono release ordering
 * correct with no extra machinery. It is the same bound, for the same reason, that
 * swing uses to stop an off-beat crossing the next on-beat (transport.md REQ-swing-offset-is-public).
 */
export const MICRO_MAX = 12;

/**
 * The bound on a seq step's `bend`, in semitones either way (step-settings.md
 * REQ-a-seq-step-carries-a-bend). An octave covers the idioms — a semitone or
 * whole-tone scoop, a dive-bomb fall — without letting a payload ask for a
 * detune nobody would call a bend.
 */
export const BEND_MAX = 12;

/* ------------------------------------------------------- sample time-stretch */
/*
 * time-stretch.md REQ-the-stretch-ratio-is-bounded. These bound a *local* edit rather than a payload, and
 * they are here anyway for the reason ADR-015 gives: a stretch ratio reaches a
 * length calculation and then an allocation, and the buffer it is applied to did
 * arrive from outside (a dropped file, a project zip, a restored IndexedDB clip).
 * A bound is written down once, in the module that is the single source of truth
 * for bounds — not as a literal beside the `new Float32Array`.
 */

/**
 * How far a clip may be retimed, as output ÷ input. Past roughly these figures an
 * overlap-add stretch stops sounding like the source and starts sounding like the
 * algorithm, so this is a *quality* floor as much as a safety one — the arithmetic
 * would survive far wider. 4× also keeps `MAX_STRETCH_OUTPUT_FRAMES` reachable
 * only from an already-long clip.
 */
export const MIN_STRETCH_RATIO = 0.25;
export const MAX_STRETCH_RATIO = 4;

/**
 * Hard cap on the frames a stretch may allocate, checked **before** the output is
 * created. The ratio alone does not bound the allocation: 4× of an hour-long clip
 * is four hours. Two minutes at 48 kHz is far past any sampler one-shot and still
 * ~46 MB per channel — refusing a real clip is the worse failure (ADR-015).
 */
export const MAX_STRETCH_OUTPUT_FRAMES = 48_000 * 120;

/**
 * Pitch-shift range in semitones. An octave either way; beyond that the resample
 * half is doing more damage than the shift is worth. Matches the ±24 span of
 * `sampler.t*.pitch` in spirit but is deliberately tighter, because that one is
 * varispeed and this one has to reconstruct what it moved.
 */
export const MAX_PITCH_SHIFT_SEMITONES = 12;

/* --------------------------------------------------------------- scratch */
/*
 * scratch.md REQ-scratch-bounds-live-in-limits. Same reasoning as the stretch bounds above, one step
 * further out: a scratch curve is *drawn*, so its point count, its rates and its
 * length are all user numbers that reach an allocation. The output allocation
 * itself reuses MAX_STRETCH_OUTPUT_FRAMES — it is the same buffer in the same
 * modal, and a second ceiling for it would be a second thing to keep honest.
 *
 * MIN_STRETCH_RATIO / MAX_STRETCH_RATIO deliberately do NOT apply: a scratch's
 * length comes from a sixteenth count, not from the source, so "a two-second clip
 * scratched into one bar" is an ordinary request rather than a ratio to refuse.
 */

/**
 * Breakpoints one curve may hold. Well past what a hand draws — a busy flare is
 * a dozen — and low enough that the O(points) normalise/prefix-sum passes stay
 * invisible next to the per-sample read they set up.
 */
export const MAX_SCRATCH_POINTS = 64;

/**
 * Fastest the platter may be pushed, in playback-rate units, either direction.
 * Two octaves up is already past what a hand does to a record, and it doubles as
 * the anti-alias tap ceiling: the box filter takes ceil(|rate|) taps, so this
 * bounds that inner loop at four (REQ-fast-reads-are-box-averaged).
 */
export const MAX_SCRATCH_RATE = 4;

/**
 * Longest scratch, in sixteenths — four bars in 4/4, the same ceiling the
 * editor's Fit targets already stop at. Combined with the frame cap above, a
 * length past this is refused before anything is allocated.
 */
export const MAX_SCRATCH_STEPS = 64;

/**
 * Keys a payload may never carry. `PatternStore.restore` does
 * `Object.assign(cell, DEFAULTS, parsedCell)`, and `Object.assign` uses [[Set]] —
 * so a `__proto__` key from `JSON.parse` re-points the destination cell's
 * prototype. Today every field read by the audio layer is an own property from
 * the defaults, so the swap is shadowed and inert; that is a coincidence of the
 * defaults covering every field, not a guarantee. Refusing the key here is what
 * makes it a guarantee.
 */
export const RESERVED_KEYS: readonly string[] = ['__proto__', 'constructor', 'prototype'];

/** Whether an object parsed from a payload carries any {@link RESERVED_KEYS}. */
export function reservedKeyIn(o: object): string | null {
  for (const k of RESERVED_KEYS) {
    if (Object.prototype.hasOwnProperty.call(o, k)) return k;
  }
  return null;
}
